import { useCallback, useEffect, useRef, useState } from 'react';
import type { Fact } from '@stonelink/shared';
import { api } from '../api';
import { Badge, Button, EmptyState, PageHeader, Textarea } from '../ui';

/**
 * The training conversation.
 *
 * Facts the trainee writes appear inline as editable cards, so confirmation is
 * both conversational ("Got it — returns within 30 days…") and inspectable: the
 * owner can see and correct exactly what went into the notebook.
 */

type Entry =
  | { kind: 'message'; id: string; role: 'user' | 'assistant'; text: string }
  | { kind: 'fact'; id: string; action: 'created' | 'updated' | 'archived'; fact: Fact }
  | { kind: 'error'; id: string; text: string };

const SUGGESTIONS = [
  "We're closed on Sundays and open 9 to 5 the rest of the week.",
  'Returns are accepted within 30 days with a receipt.',
  'Delivery inside the city is free on orders over £50.',
];

let entryCounter = 0;
const nextId = () => `e${(entryCounter += 1)}`;

export function TrainPage() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void (async () => {
      try {
        const session = await api.trainingSession();
        setConversationId(session.conversationId);
        setEntries(
          session.messages.map((message) => ({
            kind: 'message' as const,
            id: message.id,
            role: message.role,
            text: message.content,
          })),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
  }, [entries]);

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || busy) return;

      setDraft('');
      setBusy(true);
      setEntries((prev) => [
        ...prev,
        { kind: 'message', id: nextId(), role: 'user', text: message },
      ]);

      const replyId = nextId();
      let reply = '';
      setEntries((prev) => [
        ...prev,
        { kind: 'message', id: replyId, role: 'assistant', text: '' },
      ]);

      try {
        await api.trainingChat(
          conversationId ? { message, conversationId } : { message },
          (event) => {
            switch (event.type) {
              case 'start':
                setConversationId(event.conversationId);
                break;
              case 'delta':
                reply += event.text;
                setEntries((prev) =>
                  prev.map((entry) =>
                    entry.kind === 'message' && entry.id === replyId
                      ? { ...entry, text: reply }
                      : entry,
                  ),
                );
                break;
              case 'fact':
                // Insert the card *before* the streaming reply so the notebook
                // change reads as having happened first, which is what the
                // assistant's confirmation then refers to.
                setEntries((prev) => {
                  const index = prev.findIndex(
                    (entry) => entry.kind === 'message' && entry.id === replyId,
                  );
                  const card: Entry = {
                    kind: 'fact',
                    id: `${event.fact.id}-${event.action}-${nextId()}`,
                    action: event.action,
                    fact: event.fact,
                  };
                  if (index === -1) return [...prev, card];
                  return [...prev.slice(0, index), card, ...prev.slice(index)];
                });
                break;
              case 'error':
                setEntries((prev) => [
                  ...prev.filter((entry) => entry.id !== replyId),
                  { kind: 'error', id: nextId(), text: event.message },
                ]);
                break;
              default:
                break;
            }
          },
        );
      } catch {
        setEntries((prev) => [
          ...prev.filter((entry) => entry.id !== replyId),
          { kind: 'error', id: nextId(), text: 'Could not reach the trainer. Please try again.' },
        ]);
      } finally {
        setBusy(false);
      }
    },
    [busy, conversationId],
  );

  const startFresh = async () => {
    const session = await api.newTrainingSession();
    setConversationId(session.conversationId);
    setEntries([]);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Train"
        description="Talk to your assistant the way you would brief a new employee. Anything it learns is written to the Notebook."
      />

      <div className="glass flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <p className="text-sm text-muted">
            {busy ? 'Listening…' : 'Tell it something about your business.'}
          </p>
          <Button variant="secondary" onClick={() => void startFresh()} disabled={busy}>
            Start a new session
          </Button>
        </div>

        <div ref={logRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-surface p-5">
          {loading && <p className="text-sm text-muted">Loading…</p>}

          {!loading && entries.length === 0 && (
            <div className="space-y-4">
              <EmptyState
                title="Nothing taught yet"
                hint="Say something like the examples below, in your own words."
              />
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => void send(suggestion)}
                    className="rounded-full border border-line bg-white/5 px-3 py-1.5 text-left text-xs text-muted transition hover:border-brand hover:text-ink"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {entries.map((entry) => {
            if (entry.kind === 'error') {
              return (
                <div
                  key={entry.id}
                  role="alert"
                  className="mx-auto max-w-lg rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2 text-center text-sm text-red-200"
                >
                  {entry.text}
                </div>
              );
            }
            if (entry.kind === 'fact') {
              return <FactCard key={entry.id} action={entry.action} fact={entry.fact} />;
            }
            const isUser = entry.role === 'user';
            return (
              <div
                key={entry.id}
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  isUser
                    ? 'ml-auto rounded-br-md bg-brand text-[#15102a]'
                    : 'mr-auto rounded-bl-md border border-line bg-glass-strong'
                }`}
              >
                {entry.text || (isUser ? '' : <span className="text-muted">…</span>)}
              </div>
            );
          })}
        </div>

        <form
          className="flex items-end gap-2 border-t border-line p-4"
          onSubmit={(event) => {
            event.preventDefault();
            void send(draft);
          }}
        >
          <Textarea
            rows={2}
            value={draft}
            disabled={busy}
            placeholder="e.g. we're closed on Sundays"
            aria-label="What should the assistant learn?"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send(draft);
              }
            }}
          />
          <Button type="submit" disabled={busy || !draft.trim()}>
            Teach
          </Button>
        </form>
      </div>
    </div>
  );
}

/** An inspectable record of a notebook change, editable inline. */
function FactCard({ action, fact }: { action: 'created' | 'updated' | 'archived'; fact: Fact }) {
  const [content, setContent] = useState(fact.content);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archived, setArchived] = useState(action === 'archived');

  const tone = action === 'archived' ? 'warn' : action === 'updated' ? 'brand' : 'good';
  const label =
    action === 'archived'
      ? 'Removed from notebook'
      : action === 'updated'
        ? 'Corrected'
        : 'Learned';

  const save = async () => {
    setSaving(true);
    try {
      await api.updateFact(fact.id, { content });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    setSaving(true);
    try {
      await api.updateFact(fact.id, { status: 'archived' });
      setArchived(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass mx-auto w-full max-w-xl p-4">
      <div className="mb-2 flex items-center gap-2">
        <Badge tone={tone}>{label}</Badge>
        {archived && action !== 'archived' && <Badge tone="warn">Archived</Badge>}
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            rows={3}
            value={content}
            aria-label="Fact content"
            onChange={(event) => setContent(event.target.value)}
          />
          <div className="flex gap-2">
            <Button onClick={() => void save()} disabled={saving}>
              Save
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setContent(fact.content);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className={`text-sm leading-relaxed ${archived ? 'text-muted line-through' : ''}`}>
            {content}
          </p>
          {!archived && (
            <div className="mt-3 flex gap-3 text-xs">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="font-medium text-brand hover:underline"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void archive()}
                disabled={saving}
                className="font-medium text-muted hover:text-red-300 hover:underline"
              >
                Remove
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
